import React, { useState, useEffect, useRef } from 'react';
import { Lock, KeyRound, Eye, EyeOff, ShieldCheck, ArrowRight, AlertCircle, Sparkles } from 'lucide-react';
import { authenticateWithPassword } from '../../services/auth';

export interface AuthGatewayModalProps {
  isOpen: boolean;
  onAuthenticated: () => void;
}

export const AuthGatewayModal: React.FC<AuthGatewayModalProps> = ({
  isOpen,
  onAuthenticated,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim() || isLoading) return;

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const result = await authenticateWithPassword(password, rememberMe);
      if (result.success) {
        onAuthenticated();
      } else {
        setErrorMessage(result.error || 'Невірний пароль доступу');
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
        inputRef.current?.focus();
      }
    } catch {
      setErrorMessage('Помилка авторизації. Спробуйте ще раз.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0A0F1D]/95 backdrop-blur-2xl animate-fade-in select-none">
      {/* Background ambient lighting */}
      <div className="absolute w-[500px] h-[500px] bg-accent-emerald/10 rounded-full blur-3xl pointer-events-none -top-20 -left-20" />
      <div className="absolute w-[450px] h-[450px] bg-accent-cyan/10 rounded-full blur-3xl pointer-events-none -bottom-20 -right-20" />

      {/* Auth Card */}
      <div
        className={`
          relative w-full max-w-md bg-slate-900/90 border border-white/10
          rounded-3xl p-6 sm:p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8),0_0_30px_rgba(16,185,129,0.15)]
          backdrop-blur-xl transition-transform duration-300
          ${isShaking ? 'animate-bounce' : ''}
        `}
      >
        {/* Glowing Badge & Icon */}
        <div className="flex flex-col items-center text-center mb-6">
          <div className="relative mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-emerald-500 to-cyan-400 p-[1px] shadow-[0_0_25px_rgba(16,185,129,0.4)] flex items-center justify-center">
              <div className="w-full h-full bg-slate-950 rounded-2xl flex items-center justify-center">
                <Lock className="w-8 h-8 text-accent-neon" />
              </div>
            </div>
            <div className="absolute -bottom-1 -right-1 p-1 bg-slate-900 rounded-full border border-emerald-500/40">
              <ShieldCheck className="w-4 h-4 text-accent-emerald" />
            </div>
          </div>

          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-accent-neon text-xs font-semibold uppercase tracking-wider mb-2">
            <Sparkles className="w-3.5 h-3.5" />
            Закритий доступ
          </div>

          <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight">
            Starlife Simulator
          </h2>
          <p className="text-xs sm:text-sm text-text-muted mt-1 max-w-xs">
            Введіть пароль для доступу до інтерактивного симулятора структури
          </p>
        </div>

        {/* Error notification */}
        {errorMessage && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-2.5 text-xs text-red-400 animate-fade-in">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Password Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
              Пароль доступу
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-text-muted">
                <KeyRound className="w-4 h-4" />
              </div>
              <input
                ref={inputRef}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errorMessage) setErrorMessage(null);
                }}
                placeholder="Введіть ваш пароль"
                autoComplete="current-password"
                className="
                  w-full pl-10 pr-11 py-3 bg-slate-950/80 border border-white/10 rounded-xl
                  text-base sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-accent-neon
                  focus:ring-2 focus:ring-accent-emerald/30 transition-all font-mono
                "
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-text-muted hover:text-white transition-colors cursor-pointer"
                title={showPassword ? 'Сховати пароль' : 'Показати пароль'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Remember me toggle */}
          <div className="flex items-center justify-between text-xs text-text-muted pt-1">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded bg-slate-950 border-slate-700 text-accent-emerald focus:ring-accent-emerald focus:ring-offset-slate-900"
              />
              <span>Запам'ятати на цьому пристрої</span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !password.trim()}
            className="
              w-full py-3.5 px-4 rounded-xl font-bold text-sm text-slate-950
              bg-gradient-to-r from-accent-emerald to-accent-neon hover:brightness-110
              shadow-[0_0_20px_rgba(0,229,153,0.35)] active:scale-[0.98]
              transition-all duration-200 flex items-center justify-center gap-2
              cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <span>Увійти в симулятор</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        {/* Security Footer Note */}
        <div className="mt-6 pt-4 border-t border-white/5 text-center text-[11px] text-text-muted">
          Захищене з'єднання з криптографічною верифікацією сесії (Cloudflare Edge)
        </div>
      </div>
    </div>
  );
};
